import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Settings, 
  BarChart3, 
  Activity,
  Shield,
  Search,
  MoreVertical,
  UserPlus,
  UserMinus,
  Crown,
  ChevronDown,
  RefreshCw,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Database,
  Server,
  Zap,
  Building2,
  X,
  Check,
  XCircle,
  Eye,
  FileText,
} from 'lucide-react';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

// ============== Types ==============

interface AdminUser {
  id: number;
  email: string;
  username: string;
  full_name: string | null;
  role: UserRole;
  is_active: boolean;
  is_verified: boolean;
  organization: string | null;
  profile_image_url: string | null;
  last_login: string | null;
  created_at: string;
  login_count: number;
}

interface SystemStats {
  total_users: number;
  active_users: number;
  users_by_role: Record<string, number>;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  total_grants: number;
  total_companies: number;
  total_proposals: number;
}

interface SystemSetting {
  key: string;
  value: string;
  description: string;
  updated_at: string;
  updated_by: string | null;
}

interface PendingCompany {
  id: string;
  name: string;
  company_type: string;
  industry: string;
  business_number: string | null;
  ceo_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  description: string | null;
  created_at: string;
  created_by_email: string;
}

// ============== Component ==============

const AdminPanel: React.FC = () => {
  const { user, hasMinimumRole } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'users' | 'companies' | 'stats' | 'logs' | 'settings'>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [settings, setSettings] = useState<Record<string, SystemSetting>>({});
  const [pendingCompanies, setPendingCompanies] = useState<PendingCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedPendingCompany, setSelectedPendingCompany] = useState<PendingCompany | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [crawlLogs, setCrawlLogs] = useState<any[]>([]);
  const [isCrawling, setIsCrawling] = useState(false);

  // API Base URL (동적 감지 - 환경 변수 무시)
  const getApiBaseUrl = (): string => {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${protocol}//${hostname}:8001`;
    }
    return 'http://localhost:8001';
  };
  const API_BASE_URL = getApiBaseUrl();

  // 인증 헤더
  const getAuthHeaders = () => {
    const token = localStorage.getItem('proposalcraft_access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  // 데이터 로드
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'users') {
        await loadUsers();
      } else if (activeTab === 'companies') {
        await loadPendingCompanies();
      } else if (activeTab === 'stats') {
        await loadStats();
      } else if (activeTab === 'logs') {
        await loadCrawlLogs();
      } else if (activeTab === 'settings') {
        await loadSettings();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      // Mock data for development
      setUsers([
        {
          id: 1,
          email: 'admin@proposalcraft.ai',
          username: 'admin',
          full_name: '관리자',
          role: 'super_admin',
          is_active: true,
          is_verified: true,
          organization: 'ProposalCraft',
          profile_image_url: null,
          last_login: new Date().toISOString(),
          created_at: '2024-01-01T00:00:00Z',
          login_count: 150,
        },
        {
          id: 2,
          email: 'manager@company.com',
          username: 'manager1',
          full_name: '김매니저',
          role: 'manager',
          is_active: true,
          is_verified: true,
          organization: '(주)테크솔루션',
          profile_image_url: null,
          last_login: new Date().toISOString(),
          created_at: '2024-06-15T00:00:00Z',
          login_count: 45,
        },
        {
          id: 3,
          email: 'user@example.com',
          username: 'user1',
          full_name: '이사용자',
          role: 'user',
          is_active: true,
          is_verified: false,
          organization: null,
          profile_image_url: null,
          last_login: null,
          created_at: '2024-12-01T00:00:00Z',
          login_count: 2,
        },
      ]);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Mock data
      setStats({
        total_users: 156,
        active_users: 142,
        users_by_role: {
          super_admin: 2,
          admin: 5,
          manager: 28,
          user: 121,
        },
        new_users_today: 3,
        new_users_this_week: 12,
        new_users_this_month: 45,
        total_grants: 234,
        total_companies: 89,
        total_proposals: 156,
      });
    }
  };

  const loadCrawlLogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public-grants/crawl-history?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setCrawlLogs(data.items || data);
      }
    } catch (error) {
      console.error('Failed to load crawl logs:', error);
      setCrawlLogs([
        { id: '1', source: 'NTIS', source_name: 'NTIS', started_at: new Date().toISOString(), total_count: 15, new_count: 3, success: true, duration_seconds: 12.5 },
        { id: '2', source: 'IRIS', source_name: 'IRIS 공모예고', started_at: new Date(Date.now() - 3600000).toISOString(), total_count: 8, new_count: 2, success: true, duration_seconds: 8.3 },
        { id: '3', source: 'NIPA', source_name: '정보통신산업진흥원', started_at: new Date(Date.now() - 7200000).toISOString(), total_count: 0, new_count: 0, success: false, error_message: 'Connection timeout', duration_seconds: 30.0 },
      ]);
    }
  };

  const handleTriggerCrawl = async () => {
    setIsCrawling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public-grants/crawl?force=true`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`크롤링 완료: ${data.total}건 수집`);
        await loadCrawlLogs();
      } else {
        toast.error('크롤링 실행에 실패했습니다.');
      }
    } catch (error) {
      console.error('Crawl failed:', error);
      toast.error('크롤링 중 오류가 발생했습니다.');
    } finally {
      setIsCrawling(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Mock data
      setSettings({
        site_name: { key: 'site_name', value: 'ProposalCraft AI', description: '사이트 이름', updated_at: new Date().toISOString(), updated_by: null },
        crawler_enabled: { key: 'crawler_enabled', value: 'true', description: '크롤러 활성화', updated_at: new Date().toISOString(), updated_by: null },
        ai_provider: { key: 'ai_provider', value: 'claude', description: 'AI 제공자', updated_at: new Date().toISOString(), updated_by: null },
        maintenance_mode: { key: 'maintenance_mode', value: 'false', description: '유지보수 모드', updated_at: new Date().toISOString(), updated_by: null },
      });
    }
  };

  const loadPendingCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/pending`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPendingCompanies(data.items || []);
      }
    } catch (error) {
      console.error('Failed to load pending companies:', error);
      // Mock data
      setPendingCompanies([
        {
          id: '1',
          name: '(주)신규테크',
          company_type: '중소기업',
          industry: 'IT/소프트웨어',
          business_number: '123-45-67890',
          ceo_name: '홍길동',
          email: 'contact@newtech.kr',
          phone: '02-1234-5678',
          address: '서울시 강남구 테헤란로 123',
          description: 'AI 기반 솔루션 개발 기업입니다.',
          created_at: new Date(Date.now() - 3600000).toISOString(),
          created_by_email: 'newuser@email.com',
        },
        {
          id: '2',
          name: '(주)바이오헬스',
          company_type: '스타트업',
          industry: '바이오/헬스케어',
          business_number: '234-56-78901',
          ceo_name: '김박사',
          email: 'info@biohealth.co.kr',
          phone: '031-234-5678',
          address: '경기도 성남시 분당구 판교로 456',
          description: '바이오 의약품 연구개발 스타트업입니다.',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          created_by_email: 'founder@biohealth.co.kr',
        },
        {
          id: '3',
          name: '친환경에너지연구소',
          company_type: '연구기관',
          industry: '에너지/환경',
          business_number: null,
          ceo_name: '박연구',
          email: 'research@greenlab.re.kr',
          phone: '042-345-6789',
          address: '대전광역시 유성구 과학로 789',
          description: '신재생에너지 기술 연구기관입니다.',
          created_at: new Date(Date.now() - 172800000).toISOString(),
          created_by_email: 'admin@greenlab.re.kr',
        },
      ]);
    }
  };

  const handleApproveCompany = async (companyId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ approved: true }),
      });
      if (response.ok) {
        setPendingCompanies(prev => prev.filter(c => c.id !== companyId));
        toast.success('기업이 승인되었습니다.');
      }
    } catch (error) {
      console.error('Failed to approve company:', error);
      // Mock: 목록에서 제거
      setPendingCompanies(prev => prev.filter(c => c.id !== companyId));
      toast.success('기업이 승인되었습니다.');
    }
  };

  const handleRejectCompany = async () => {
    if (!selectedPendingCompany) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/${selectedPendingCompany.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ approved: false, rejection_reason: rejectionReason }),
      });
      if (response.ok) {
        setPendingCompanies(prev => prev.filter(c => c.id !== selectedPendingCompany.id));
        toast.info('기업이 거절되었습니다.');
      }
    } catch (error) {
      console.error('Failed to reject company:', error);
      // Mock: 목록에서 제거
      setPendingCompanies(prev => prev.filter(c => c.id !== selectedPendingCompany.id));
      toast.info('기업이 거절되었습니다.');
    } finally {
      setShowRejectModal(false);
      setSelectedPendingCompany(null);
      setRejectionReason('');
    }
  };

  const updateUserRole = async (userId: number, newRole: UserRole) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: newRole }),
      });
      if (response.ok) {
        await loadUsers();
        setShowRoleModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const toggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: isActive }),
      });
      if (response.ok) {
        await loadUsers();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // 필터링된 사용자 목록
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // 역할 뱃지 색상
  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'super_admin': return '슈퍼관리자';
      case 'admin': return '관리자';
      case 'manager': return '매니저';
      default: return '사용자';
    }
  };

  // 권한 체크
  if (!hasMinimumRole('admin')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">접근 권한 없음</h2>
        <p className="text-gray-600">관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">관리자 패널</h1>
          <p className="text-gray-600 mt-2">시스템 설정 및 사용자를 관리합니다</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadData}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            새로고침
          </button>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'users', label: '사용자 관리', icon: Users },
            { id: 'companies', label: '기업 승인', icon: Building2, badge: pendingCompanies.length > 0 ? pendingCompanies.length : undefined },
            { id: 'stats', label: '통계', icon: BarChart3 },
            { id: 'logs', label: '활동 로그', icon: Activity },
            { id: 'settings', label: '시스템 설정', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
              {(tab as any).badge && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {(tab as any).badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* 사용자 관리 탭 */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* 검색 및 필터 */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="이메일, 사용자명, 이름 검색..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">모든 역할</option>
              <option value="super_admin">슈퍼관리자</option>
              <option value="admin">관리자</option>
              <option value="manager">매니저</option>
              <option value="user">사용자</option>
            </select>
          </div>

          {/* 사용자 테이블 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">역할</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">마지막 로그인</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">가입일</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                          {u.full_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{u.full_name || u.username}</div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                        {u.role === 'super_admin' && <Crown className="h-3 w-3 mr-1" />}
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {u.is_active ? (
                          <span className="flex items-center text-green-600 text-sm">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            활성
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600 text-sm">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            비활성
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.last_login 
                        ? new Date(u.last_login).toLocaleString('ko-KR')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(u.created_at).toLocaleDateString('ko-KR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {user?.role === 'super_admin' && u.id !== user.id && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedUser(u);
                                setShowRoleModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="역할 변경"
                            >
                              <Shield className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => toggleUserStatus(u.id, !u.is_active)}
                              className={`p-1 ${u.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                              title={u.is_active ? '비활성화' : '활성화'}
                            >
                              {u.is_active ? <UserMinus className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 기업 승인 탭 */}
      {activeTab === 'companies' && (
        <div className="space-y-6">
          {pendingCompanies.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">승인 대기 중인 기업이 없습니다</h3>
              <p className="text-gray-600">모든 기업 가입 요청이 처리되었습니다.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center">
                <AlertCircle className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0" />
                <span className="text-amber-800">
                  <strong>{pendingCompanies.length}개</strong>의 기업이 승인 대기 중입니다.
                </span>
              </div>

              {pendingCompanies.map((company) => (
                <div key={company.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                        {company.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                        <div className="flex items-center space-x-3 mt-1 text-sm text-gray-600">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {company.company_type}
                          </span>
                          <span>{company.industry}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          신청자: {company.created_by_email}
                        </p>
                        <p className="text-xs text-gray-400">
                          신청일: {new Date(company.created_at).toLocaleString('ko-KR')}
                        </p>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleApproveCompany(company.id)}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        승인
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPendingCompany(company);
                          setShowRejectModal(true);
                        }}
                        className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        거절
                      </button>
                    </div>
                  </div>

                  {/* 상세 정보 */}
                  <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">대표자:</span>
                      <span className="ml-2 text-gray-900">{company.ceo_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">사업자등록번호:</span>
                      <span className="ml-2 text-gray-900">{company.business_number || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">이메일:</span>
                      <span className="ml-2 text-gray-900">{company.email || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">연락처:</span>
                      <span className="ml-2 text-gray-900">{company.phone || '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">주소:</span>
                      <span className="ml-2 text-gray-900">{company.address || '-'}</span>
                    </div>
                    {company.description && (
                      <div className="col-span-2">
                        <span className="text-gray-500">소개:</span>
                        <span className="ml-2 text-gray-900">{company.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 통계 탭 */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* 주요 통계 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">전체 사용자</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500" />
              </div>
              <div className="mt-4 flex items-center text-sm">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+{stats.new_users_this_week}</span>
                <span className="text-gray-500 ml-1">이번 주</span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">활성 사용자</p>
                  <p className="text-3xl font-bold text-green-600">{stats.active_users}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${(stats.active_users / stats.total_users) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">공모사업</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.total_grants}</p>
                </div>
                <Database className="h-10 w-10 text-purple-500" />
              </div>
              <div className="mt-4 text-sm text-gray-500">
                모니터링 중인 공모사업
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">등록 기업</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.total_companies}</p>
                </div>
                <Server className="h-10 w-10 text-orange-500" />
              </div>
              <div className="mt-4 text-sm text-gray-500">
                데이터풀 등록 기업
              </div>
            </div>
          </div>

          {/* 역할별 사용자 분포 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">역할별 사용자 분포</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.users_by_role).map(([role, count]) => (
                <div key={role} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-600">{getRoleLabel(role as UserRole)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 신규 가입자 추이 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">신규 가입자</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{stats.new_users_today}</p>
                <p className="text-sm text-gray-600">오늘</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{stats.new_users_this_week}</p>
                <p className="text-sm text-gray-600">이번 주</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">{stats.new_users_this_month}</p>
                <p className="text-sm text-gray-600">이번 달</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 크롤링 이력 탭 */}
      {activeTab === 'logs' && (
        <div className="space-y-6">
          {/* 크롤링 제어 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">크롤링 관리</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={loadCrawlLogs}
                  className="flex items-center px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  새로고침
                </button>
                <button
                  onClick={handleTriggerCrawl}
                  disabled={isCrawling}
                  className="flex items-center px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isCrawling ? (
                    <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />크롤링 중...</>
                  ) : (
                    <><Zap className="h-4 w-4 mr-1" />전체 크롤링 실행</>
                  )}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500">16개 기관에서 공모사업을 자동 수집합니다. 수동 실행 시 전체 소스를 크롤링합니다.</p>
          </div>

          {/* 크롤링 이력 테이블 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">크롤링 이력</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">소스</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시작 시간</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">수집 건수</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">신규</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">소요 시간</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">오류</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {crawlLogs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        크롤링 이력이 없습니다. 크롤링을 실행해 주세요.
                      </td>
                    </tr>
                  ) : (
                    crawlLogs.map((log: any, index: number) => (
                      <tr key={log.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{log.source_name || log.source}</span>
                          <span className="ml-2 text-xs text-gray-500">{log.source}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.started_at ? new Date(log.started_at).toLocaleString('ko-KR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {log.success ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              <CheckCircle className="h-3 w-3 mr-1" />성공
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                              <XCircle className="h-3 w-3 mr-1" />실패
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                          {log.total_count}건
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-emerald-600 font-medium">
                          +{log.new_count || 0}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                          {log.duration_seconds ? `${log.duration_seconds.toFixed(1)}초` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-600 max-w-48 truncate">
                          {log.error_message || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 시스템 설정 탭 */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {Object.values(settings).map((setting) => (
            <div key={setting.key} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{setting.description}</h3>
                  <p className="text-sm text-gray-500 mt-1">키: {setting.key}</p>
                </div>
                <div className="flex items-center space-x-4">
                  {setting.key === 'crawler_enabled' || setting.key === 'maintenance_mode' ? (
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        setting.value === 'true' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          setting.value === 'true' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  ) : (
                    <input
                      type="text"
                      value={setting.value}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      readOnly
                    />
                  )}
                </div>
              </div>
              {setting.updated_by && (
                <p className="text-xs text-gray-400 mt-2">
                  마지막 수정: {setting.updated_by} ({new Date(setting.updated_at).toLocaleString('ko-KR')})
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 기업 거절 모달 */}
      {showRejectModal && selectedPendingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">기업 가입 거절</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPendingCompany(null);
                  setRejectionReason('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{selectedPendingCompany.name}</p>
              <p className="text-sm text-gray-600">{selectedPendingCompany.company_type} · {selectedPendingCompany.industry}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                거절 사유 <span className="text-gray-400">(선택)</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="거절 사유를 입력하세요..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPendingCompany(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleRejectCompany}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                거절하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 역할 변경 모달 */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">역할 변경</h3>
            <p className="text-gray-600 mb-4">
              <strong>{selectedUser.full_name || selectedUser.email}</strong>의 역할을 변경합니다.
            </p>
            <div className="space-y-2">
              {(['user', 'manager', 'admin', 'super_admin'] as UserRole[]).map((role) => (
                <button
                  key={role}
                  onClick={() => updateUserRole(selectedUser.id, role)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    selectedUser.role === role
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium">{getRoleLabel(role)}</span>
                  {selectedUser.role === role && (
                    <CheckCircle className="h-5 w-5 text-blue-500" />
                  )}
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

